import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import './hostedCardPanel.css';
import visaMark from './cardMarks/visa.svg';
import mastercardMark from './cardMarks/mastercard.svg';
import amexMark from './cardMarks/amex.svg';
import discoverMark from './cardMarks/discover.svg';
import dinersMark from './cardMarks/diners.svg';
import applePayMark from './cardMarks/applepay.svg';
import samsungPayMark from './cardMarks/samsungpay.svg';

/**
 * HostedCardPanel — the EMBEDDED card form (N-Genius Hosted Session / Web SDK).
 *
 * The whole point (2026-08-13): the client pays ON OUR PAGE. No redirect,
 * no single-use paypage URL to die on a Safari tab-restore, no "payment link
 * does not exist", no client stranded on a gateway domain. Card data is
 * captured inside N-Genius-hosted iframes (never touches our origin); we get
 * a one-time sessionId, the surface's own endpoint completes the payment
 * server-side, the SDK runs any 3DS challenge in OUR overlay, and the server
 * settles from order truth via POST /payments/verify.
 *
 * Contract with the parent surface:
 *   startPayment(sessionId) → Promise<{ order_ref, payment_response, ... }>
 *     The parent calls its own endpoint (e.g. /payments/hosted-session/topup)
 *     — this panel never knows amounts or purposes.
 *   onPaid(result)     — verified 'paid' outcome (result = /verify response).
 *   onFailed(result)   — verified terminal failure.
 *   onUnavailable()    — rail not enabled / SDK failed: the parent MUST fall
 *     back to the redirect paypage button. Never render a broken embed.
 *   payLabel           — button text, e.g. "Pay AED 512.50".
 *   disabled           — parent-side gate (e.g. amount below minimum).
 *   configUrl          — where the SDK config comes from. Defaults to the
 *     authenticated client endpoint; the public pay link (/pay/<token>) passes
 *     its own token-scoped one, because a guest has no session to authenticate.
 *   verifyPayment(orderRef) → Promise<{outcome,…}>
 *     How this surface asks the SERVER what happened. Defaults to POST
 *     /payments/verify; the public page overrides it with its token-scoped
 *     equivalent. Whatever it is, SERVER TRUTH still decides the outcome —
 *     never the SDK's status.
 *
 * The SDK authenticates for ~5 minutes; a stale mount is remounted on the
 * next failure rather than pre-emptively (simplest honest recovery).
 */

/*
 * WARM-UP (2026-08-29). Nothing here changes what the panel does — only when
 * the waiting happens.
 *
 * Opening the form used to run three network steps strictly in series, all of
 * them AFTER the payer pressed Pay: fetch our config, fetch N-Genius's SDK
 * from an origin the browser has never spoken to (DNS + TLS + the script), and
 * only then build the iframes. On a phone on mobile data that is the whole of
 * "Loading secure card form…".
 *
 * The pages that own a pay button know the payer is going to press it, so the
 * first two steps can happen while they are still reading the amount. By the
 * time the panel mounts the config is in this cache and window.NI already
 * exists, leaving only the iframe build.
 *
 * Fail-soft by construction: a warm-up that errors is swallowed and the panel's
 * own boot does exactly what it always did.
 */
const configCache = new Map();

export function warmHostedSession(configUrl) {
    if (!configUrl) return;
    try {
        // The one thing that helps before we even know the SDK's URL: let the
        // browser open the connection to the gateway while our config is in
        // flight. Both hosts, because which one this is depends on the server.
        for (const host of ['https://paypage.ngenius-payments.com', 'https://paypage.sandbox.ngenius-payments.com']) {
            if (document.querySelector(`link[rel="preconnect"][href="${host}"]`)) continue;
            const l = document.createElement('link');
            l.rel = 'preconnect';
            l.href = host;
            l.crossOrigin = 'anonymous';
            document.head.appendChild(l);
        }
    } catch { /* head is not writable — nothing lost */ }

    if (configCache.has(configUrl)) return;
    const p = axios.get(configUrl)
        .then((res) => {
            const cfg = res.data;
            if (!cfg?.enabled || !cfg.sdkUrl) return cfg;
            // Swallowed: a failed pre-load must not poison the cached config,
            // and the panel will simply load the SDK itself.
            loadSdk(cfg.sdkUrl).catch(() => {});
            return cfg;
        })
        .catch((e) => { configCache.delete(configUrl); throw e; });
    configCache.set(configUrl, p);
}

let sdkLoadPromise = null;
function loadSdk(url) {
    if (window.NI && typeof window.NI.mountCardInput === 'function') return Promise.resolve();
    if (sdkLoadPromise) return sdkLoadPromise;
    sdkLoadPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = url;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => { sdkLoadPromise = null; reject(new Error('SDK failed to load')); };
        document.head.appendChild(s);
    });
    return sdkLoadPromise;
}

const MOUNT_ID = 'ni-hosted-card-mount';
const THREE_DS_MOUNT_ID = 'ni-hosted-3ds-mount';
/*
 * SAMSUNG PAY DRAWS ITSELF INTO A DIV WE MUST PROVIDE.
 *
 * generateSessionId() was being called with no arguments. For a card, and for
 * Apple Pay (whose sheet Apple has already run by then), that is right — there
 * is nothing to render. Samsung Pay is different: the SDK destructures
 * { mountId, containerId } from the first argument and renders its own iframe
 * into that element, so with no argument it has nowhere to go and the payment
 * cannot start. The container is revealed by the SDK (it sets display:block)
 * and hidden again by us once the session resolves.
 */
const SAMSUNG_MODAL_ID = 'ni-hosted-samsung-modal';
const SAMSUNG_MOUNT_ID = 'ni-hosted-samsung-mount';

/* The five schemes this outlet accepts (same set the N-Genius paypage shows).
   Real brand artwork, bundled with the app — no external requests, no
   broken-image states, no hand-drawn approximations. */
function SchemeMarks() {
    return (
        /* CARD SCHEMES ONLY. Apple Pay and Samsung Pay used to sit here too,
           which put wallet marks next to the card form and left the line that
           actually explains them bare (2026-08-29). They now live on that line
           instead, where they are the subject of the sentence — and dropping
           two tiles gives "Secure payment" room to stay on a phone.

           Uniform tiles (2026-08-13): every mark occupies the SAME white,
           bordered card — the brand glyph centred inside it — so the row reads
           as one set instead of five different shapes. Amex and Discover ship
           as full card artwork, so they fill their tile. */
        <span className="hcp__schemes" aria-label="Visa, Mastercard, American Express, Discover and Diners Club accepted">
            <span className="hcp__schemeTile"><img className="hcp__scheme hcp__scheme--glyph hcp__scheme--wordmark" src={visaMark} alt="" aria-hidden="true" /></span>
            <span className="hcp__schemeTile"><img className="hcp__scheme hcp__scheme--glyph" src={mastercardMark} alt="" aria-hidden="true" /></span>
            <span className="hcp__schemeTile hcp__schemeTile--art"><img className="hcp__scheme hcp__scheme--art" src={amexMark} alt="" aria-hidden="true" /></span>
            <span className="hcp__schemeTile hcp__schemeTile--art"><img className="hcp__scheme hcp__scheme--art" src={discoverMark} alt="" aria-hidden="true" /></span>
            <span className="hcp__schemeTile"><img className="hcp__scheme hcp__scheme--glyph" src={dinersMark} alt="" aria-hidden="true" /></span>
        </span>
    );
}

const DEFAULT_CONFIG_URL = '/api/payments/hosted-session/config';
const defaultVerify = (orderRef) =>
    axios.post('/api/payments/verify', { order_ref: orderRef }).then((r) => r.data);

export default function HostedCardPanel({
    payLabel, amountAed = 0, disabled = false, startPayment, onPaid, onFailed, onUnavailable,
    configUrl = DEFAULT_CONFIG_URL, verifyPayment = defaultVerify, children,
    // When the host has a Back/Continue row, the Pay button is PORTALLED into
    // it so the step ends in one consistent [Back][Pay] row (2026-08-14).
    // The button keeps every bit of its state here — the SDK's field validity,
    // the wallet selection, paying/confirming/locked — because only this
    // component knows them. No slot: it renders in place, as before.
    actionSlot = null,
    // WALLETS (Apple Pay & friends) — on for a payment, OFF where the point is
    // to STORE a card: a wallet payment gives us no token to save, so offering
    // the Digital Payments tab on the add-a-card surface would let a client
    // "add a card" and end up with nothing on file. Read once, at mount: the
    // SDK draws its tab bar from this and never re-reads it.
    walletsEnabled = true,
    /**
     * UNDOCUMENTED SDK OPTIONS, found by reading sdk.js (2026-08-28).
     *
     * mountCardInput also accepts `preferredPaymentMethod`, `offerOnly` and
     * `paymentMethodOrder`. None are in the published API reference; the SDK
     * simply forwards them to the iframe app, so their exact semantics live at
     * N-Genius's end and could change without notice.
     *
     * They are OPT-IN per surface for that reason. The account pay link uses
     * them to open on the wallet tab — the payer arrives from a WhatsApp
     * reminder on a phone, where Apple Pay is the whole point and a card form
     * is the fallback. Everywhere else keeps the default (card first).
     *
     * If a future SDK build ignores them, the panel degrades to the ordinary
     * tabbed widget. That is a worse first tap, not a broken page.
     */
    preferredPaymentMethod = null,
    offerOnly = null,
    paymentMethodOrder = null,
}) {
    // 'loading' → 'ready' | gone (parent falls back after onUnavailable)
    const [phase, setPhase] = useState('loading');
    const [paying, setPaying] = useState(false);
    const [threeDsActive, setThreeDsActive] = useState(false);
    const [fieldsValid, setFieldsValid] = useState(false);
    const [error, setError] = useState('');
    // Confirming = the payment is placed and we are polling the server for its
    // outcome. Locked = terminal-unknown (bank still deciding): Pay must stay
    // dead so a client can never pay twice for one attempt.
    const [confirming, setConfirming] = useState(false);
    const [locked, setLocked] = useState(false);
    // Bumping this replaces the mount NODE, which destroys the SDK's iframe.
    // After a declined/failed attempt the old iframe keeps showing "Payment is
    // under process" and refuses new input — a client staring at a dead form
    // after a clear failure (2026-08-13). A fresh node = a fresh form.
    const [mountKey, setMountKey] = useState(0);
    // Which tab the SDK is showing: 'CARD' or a wallet ('APPLE_PAY', …). The
    // wallet buttons are drawn and driven by the SDK itself, so our own Pay
    // button must step aside while one is selected (2026-08-13).
    const [payMethod, setPayMethod] = useState('CARD');
    // A wallet TAB being open is not a wallet being CHOSEN — Pay stayed live
    // on Digital Payments with nothing selected (2026-08-13).
    const [walletChosen, setWalletChosen] = useState(false);
    const configRef = useRef(null);
    const deadRef = useRef(false);
    // HEIGHT OF THE CARD FORM, measured from the box it lives in.
    //
    // The SDK's iframe is height:100%, so the container's height IS the form's
    // height, and the SDK never grows it. It also re-lays itself out by its OWN
    // width: wide enough and expiry sits beside CVV (three rows), narrow and
    // they stack (four). One fixed number therefore either clips the
    // card-holder name on a phone or leaves a band of white space on a desktop.
    //
    // The SDK gives us nothing to measure against: its iframe is cross-origin,
    // and the only messages it posts are APP_READY, AUTHENTICATION_SUCCESSFULL,
    // VALIDITY_STATUS_CHANGED and PAYMENT_METHOD_CHANGE — no height, no resize.
    // So the narrow figure is deliberately GENEROUS rather than a tight fit:
    // real phones draw taller rows than a desktop browser at the same width
    // (16px minimum input text, iOS control padding, the wallet button when the
    // device supports Apple Pay), and every wrong guess so far has been a
    // clipped field, which costs the client the checkout. Spare white space
    // inside the box costs nothing.
    //
    // THE WALLET TAB IS A DIFFERENT SHAPE (2026-08-28). Switching to Digital
    // Payments swaps four rows of card fields for a short list of wallets and
    // one Apple Pay button — inside the SAME fixed-height box. At the card
    // height that leaves the button stranded below a band of white space, and
    // on a phone it can sit below the fold entirely, which is the one control
    // the payer came to press. PAYMENT_METHOD_CHANGE is the only signal the SDK
    // gives us (it posts no height), so the tab drives the height directly.
    /*
     * WHICH WALLET TO NAME.
     *
     * ONLY A PHONE GETS A SINGLE ANSWER. It can offer exactly one of them, so
     * naming the other is a wrong instruction — an iPhone will never show
     * Samsung Pay in that tab, and an Android will never show Apple Pay.
     *
     * EVERY DESKTOP BROWSER KEEPS BOTH, Safari on a Mac included. A Mac can
     * run Apple Pay itself, but the tab is also reachable by a payer who will
     * scan the code with whichever phone is in their pocket — so the desktop
     * cannot know which of the two the person will actually use, and should
     * not pretend to.
     *
     * The user-agent test only ever chooses a LABEL. It gates nothing, so a
     * misread costs a slightly odd sentence, never a failed payment. Read once
     * at mount so the wording cannot change under the payer.
     */
    const namedWallet = useRef((() => {
        if (typeof navigator === 'undefined') return 'both';
        const ua = navigator.userAgent || '';
        if (/iphone|ipod|ipad/i.test(ua)) return 'apple';
        if (/android/i.test(ua)) return 'samsung';
        return 'both';
    })()).current;

    const fieldsRef = useRef(null);
    const [fieldsHeight, setFieldsHeight] = useState(310);
    useEffect(() => {
        const el = fieldsRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const measure = () => {
            // Wallets: the list plus the button, and nothing else.
            if (payMethod !== 'CARD') return setFieldsHeight(210);
            // 460px is the narrowest width measured drawing the three-row layout.
            return setFieldsHeight(el.getBoundingClientRect().width >= 460 ? 330 : 470);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [mountKey, payMethod]);
    // Latest callbacks live in a ref so the mount effect can run EXACTLY ONCE:
    // inline arrow props change identity on every parent render (e.g. the
    // save-card checkbox), and a dep-driven remount stacked a second card form
    // (caught live, 2026-08-13).
    const cbRef = useRef({});
    cbRef.current = { startPayment, onPaid, onFailed, onUnavailable, verifyPayment };
    // Read once, at mount, for the same reason the callbacks live in a ref: the
    // boot effect must not re-run.
    const configUrlRef = useRef(configUrl);
    const walletsEnabledRef = useRef(walletsEnabled);

    const giveUp = useCallback(() => {
        if (deadRef.current) return;
        deadRef.current = true;
        setPhase('unavailable');
        cbRef.current.onUnavailable?.();
    }, []);

    // The Apple Pay sheet is drawn by Apple from THIS figure, so it must be
    // the exact amount we are about to capture. Kept in a ref: the SDK reads
    // it once at mount, and we remount when the amount changes.
    const amountRef = useRef(amountAed);
    amountRef.current = amountAed;

    /* Read ONCE at mount, for the same reason amountRef exists: the SDK takes
       these when it builds the iframe and never re-reads them, and adding them
       to mountFields' deps would remount the form (and stack a second one)
       every time the parent re-rendered. */
    const sdkOptsRef = useRef({ preferredPaymentMethod, offerOnly, paymentMethodOrder });

    const mountFields = useCallback(() => {
        const cfg = configRef.current;
        if (!cfg || !window.NI) return giveUp();
        try {
            window.NI.mountCardInput(MOUNT_ID, {
                // See the prop docs — undocumented, opt-in, and omitted
                // entirely when not asked for so nothing changes elsewhere.
                ...(sdkOptsRef.current.preferredPaymentMethod
                    ? { preferredPaymentMethod: sdkOptsRef.current.preferredPaymentMethod } : {}),
                ...(sdkOptsRef.current.offerOnly
                    ? { offerOnly: sdkOptsRef.current.offerOnly } : {}),
                ...(sdkOptsRef.current.paymentMethodOrder
                    ? { paymentMethodOrder: sdkOptsRef.current.paymentMethodOrder } : {}),
                // Themed to the portal (2026-08-13 — the SDK's unstyled
                // default is serif browser CSS). Schema: docs/web-sdk-api-reference.
                style: {
                    main: { padding: '0' },
                    base: {
                        color: '#1f2937',
                        fontFamily: 'Roboto',
                        fontUrl: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap',
                        fontSize: '15px',
                        fontWeight: '400',
                    },
                    input: {
                        color: '#111827',
                        backgroundColor: '#ffffff',
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        borderColor: '#d7dbe0',
                        borderRadius: '10px',
                        // PADDING SIZES THE FIELDS, NOT A FIXED HEIGHT.
                        //
                        // The card-number field is not a bare input: the SDK
                        // draws the detected scheme's logo inside it, so it is
                        // an input nested in a wrapper. A fixed `height` lands
                        // on a different element there than it does on expiry,
                        // CVV and name, which is why that one row came out
                        // shorter than the rest (2026-08-29).
                        //
                        // Padding plus font-size gives every field the same box
                        // whatever is nested inside it. There is no boxSizing
                        // option for `input` in the SDK's style schema — only
                        // `main` accepts it — so this is the way to make them
                        // agree.
                        padding: '14px',
                        fontSize: '15px',
                    },
                    invalid: {
                        color: '#b42318',
                        borderColor: '#e5484d',
                    },
                },
                // The deployed SDK reads `apiKey` (its docs say hostedSessionApiKey —
                // verified against the sandbox SDK 2026-08-13: only apiKey mounts).
                apiKey: cfg.hostedSessionApiKey,
                hostedSessionApiKey: cfg.hostedSessionApiKey,
                outletRef: cfg.outletRef,
                language: 'en',
                // WALLETS (Apple Pay / Samsung Pay). The SDK renders the wallet
                // buttons above the card fields when the device supports them
                // AND the outlet has the wallet enabled; otherwise the client
                // simply sees the card form exactly as before.
                multiplePaymentMethods: walletsEnabledRef.current,
                orderDetails: {
                    amount: Math.round(Number(amountRef.current || 0) * 100),
                    currency: 'AED',
                    currencyCode: 'AED',
                    // PLAIN NUMBER, no currency code: Apple's SDK validates this
                    // string and threw "AED 2500.00 is not a valid amount",
                    // killing the payment sheet (2026-08-13). N-Genius's own
                    // example is formattedAmount: '1.00'.
                    formattedAmount: Number(amountRef.current || 0).toFixed(2),
                },
                onChangePaymentMethod: ({ selectedPaymentType, selectedDigitalPayment } = {}) => {
                    setPayMethod(selectedDigitalPayment || selectedPaymentType || 'CARD');
                    setWalletChosen(!!selectedDigitalPayment);
                },
                onSuccess: () => setPhase('ready'),
                onFail: () => giveUp(),
                onChangeValidStatus: ({ isPanValid, isExpiryValid, isCVVValid, isNameValid }) => {
                    // All four — the SDK renders a card-holder name field too.
                    setFieldsValid(!!(isPanValid && isExpiryValid && isCVVValid && isNameValid));
                },
            });
        } catch {
            giveUp();
        }
    }, [giveUp]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Warmed by the surrounding page where there is one (see
                // warmHostedSession); an unwarmed panel fetches it here exactly
                // as before.
                if (!configCache.has(configUrlRef.current)) {
                    configCache.set(configUrlRef.current,
                        axios.get(configUrlRef.current).then((r) => r.data));
                }
                let cfg;
                try {
                    cfg = await configCache.get(configUrlRef.current);
                } catch (e) {
                    configCache.delete(configUrlRef.current);
                    throw e;
                }
                if (cancelled) return;
                if (!cfg?.enabled || !cfg.sdkUrl || !cfg.hostedSessionApiKey || !cfg.outletRef) return giveUp();
                configRef.current = cfg;
                await loadSdk(cfg.sdkUrl);
                if (cancelled) return;
                mountFields();
            } catch {
                if (!cancelled) giveUp();
            }
        })();
        return () => {
            cancelled = true;
            try { window.NI?.unMountCardInputs?.(); } catch { /* already gone */ }
        };
        // Mount ONCE — callbacks flow through cbRef, never through deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A clean slate after a TERMINAL failure: kill the consumed session's
    // iframe and mount a brand-new card form the client can type into again.
    /*
     * A clean slate — and it must LOOK like one.
     *
     * Two things went wrong when this ran after a WALLET attempt (live,
     * 2026-08-29: cancel the Samsung sheet and the panel came back blank).
     * The tab was still on the wallet, so the box was still the wallet's 210px
     * while the SDK drew a full card form into an iframe that is height:100% of
     * it — the form existed and was cut off. And `phase` was still 'ready', so
     * there was no scrim over the moment the new iframes take to arrive: an
     * empty white box with nothing in it and nothing happening.
     *
     * So the reset now goes all the way back to the card tab, with the loading
     * scrim up, exactly as the panel looks the first time it opens.
     */
    const resetForRetry = useCallback(() => {
        setFieldsValid(false);
        setPayMethod('CARD');
        setWalletChosen(false);
        setPhase('loading');
        try { window.NI?.unMountCardInputs?.(); } catch { /* already gone */ }
        setMountKey((k) => k + 1);
    }, []);

    /*
     * SAMSUNG PAY'S SHEET IS A MODAL, and WE open and close it — not the SDK.
     *
     * The SDK only ever reveals its container (display:block) and never puts it
     * back, so the sheet landed in the page flow between the head band and the
     * card fields: no overlay, no backdrop, the layout shoved sideways, and if
     * generateSessionId REJECTED (the usual dismiss-the-sheet case) the orphan
     * iframe stayed on the page with no way to get rid of it (2026-08-29).
     *
     * Driving the display ourselves fixes both: the sheet comes up as an
     * overlay the moment Pay is pressed — before the SDK has drawn anything, so
     * there is no flash of an unstyled block — and it always goes away again,
     * on success, on failure and on cancel, because the close lives in the
     * `finally`.
     */
    const samsungAbortRef = useRef(false);
    /*
     * STATE, NOT A DOM WRITE. Setting display straight on the node looked
     * equivalent and was not: this component re-renders while the sheet is up
     * (the width observer, the SDK's validity callbacks), and every one of
     * those renders re-applied the JSX's own `display: none` and shut the
     * sheet in the payer's face mid-payment. Caught on the tester, 2026-08-29,
     * by the sheet vanishing on its own a second after it opened.
     */
    const [samsungOpen, setSamsungOpen] = useState(false);
    const showSamsungSheet = useCallback((open) => {
        setSamsungOpen(!!open);
        const el = document.getElementById(SAMSUNG_MODAL_ID);
        /*
         * Start empty. The SDK removes its frame itself when the token
         * succeeds or fails, but NOT when the payer cancels inside Samsung's
         * sheet — that branch resolves and leaves the dead frame in our mount
         * — so a second attempt would stack a second one on top of it.
         * Clearing on the way IN and not on the way out keeps us from pulling
         * a frame out from under an SDK handler that is still holding it.
         */
        if (open) {
            const mount = document.getElementById(SAMSUNG_MOUNT_ID);
            if (mount) mount.replaceChildren();
        }
        if (el) el.style.display = open ? 'block' : 'none';
        // The page behind a full-screen sheet must not scroll under the thumb.
        try { document.body.classList.toggle('hcp-noscroll', !!open); } catch { /* SSR */ }
    }, []);

    /*
     * Backing out. The SDK's promise is still in flight and may yet resolve
     * with a perfectly good session, so the flag — checked the moment it
     * returns — is what guarantees no payment is ever STARTED for a sheet the
     * payer has closed. Nothing has been charged at this point: the session is
     * minted before startPayment, never after.
     */
    const cancelSamsung = useCallback(() => {
        samsungAbortRef.current = true;
        showSamsungSheet(false);
        setPaying(false);
        resetForRetry();
    }, [showSamsungSheet, resetForRetry]);

    /*
     * THE ONLY CONTROL OF OURS ON THIS SHEET. Samsung's own frame carries the
     * close the payer will actually use; Escape is here for the case their
     * frame never draws, which would otherwise leave a transparent layer over
     * the page with no way past it. Invisible on purpose — a visible button of
     * ours next to theirs is the modal-inside-a-modal we just removed.
     */
    useEffect(() => {
        if (!samsungOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') cancelSamsung(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [samsungOpen, cancelSamsung]);

    // Mount into the NEW node once React has swapped it in.
    useEffect(() => {
        if (mountKey === 0) return; // first mount is handled by the boot effect
        mountFields();
        /*
         * WATCHDOG. The scrim above is only honest if something takes it down
         * again: onSuccess does that on every remount we have seen, but a
         * spinner that never stops is a worse failure than the blank box it
         * replaced, and this path is reached precisely when the SDK has just
         * been through something unusual. Six seconds is far longer than a
         * remount of an already-loaded SDK takes, so in practice this never
         * fires — it exists so that it cannot get stuck.
         */
        const t = setTimeout(() => setPhase((ph) => (ph === 'loading' ? 'ready' : ph)), 6000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mountKey]);

    const pay = async () => {
        // A wallet (Apple Pay) has no fields of ours to validate; only the
        // CARD tab does. This guard ignored that, so pressing Pay with Apple
        // Pay selected silently returned (2026-08-13).
        if (paying || disabled) return;
        if (payMethod === 'CARD' ? !fieldsValid : !walletChosen) return;
        setError('');
        setPaying(true);
        if (payMethod === 'SAMSUNG_PAY') {
            samsungAbortRef.current = false;
            showSamsungSheet(true);
        }
        try {
            // Only Samsung Pay needs a mount; passing one for the other rails
            // would put an empty iframe on the page.
            const session = await window.NI.generateSessionId(
                payMethod === 'SAMSUNG_PAY'
                    ? { mountId: SAMSUNG_MOUNT_ID, containerId: SAMSUNG_MODAL_ID }
                    : undefined
            );
            // Closed the sheet while it was working: no session is used, no
            // payment is started, and cancelSamsung has already tidied up.
            if (samsungAbortRef.current) return;
            const sessionId = session?.session_id;
            showSamsungSheet(false);
            if (!sessionId) throw new Error('no session');

            const started = await cbRef.current.startPayment(sessionId);
            if (!started) { setPaying(false); return; } // parent surfaced its own error

            // The SDK owns the 3DS moment: when the bank demands a challenge it
            // mounts the issuer page into our overlay. No redirect, ever.
            setThreeDsActive(true);
            let sdkStatus = null;
            try {
                // style sizes only the legacy 3DS1 frame (fixed px, capped by
                // max-width:100%); a 3DS2 frame fills the mount, so the mount's
                // CSS height is what gives the bank's page room.
                const handled = await window.NI.handlePaymentResponse(started.payment_response, {
                    mountId: THREE_DS_MOUNT_ID,
                    style: { width: 500, height: 600 },
                });
                sdkStatus = handled?.status;
            } finally {
                setThreeDsActive(false);
            }

            // SERVER TRUTH decides the outcome (payment-outcome contract): the
            // SDK status is advisory; /verify re-reads the order and settles.
            // Poll for up to ~90s: 8 seconds was not remotely enough for a
            // real 3DS payment and left the client staring at "under process"
            // with a live Pay button (live test 2026-08-13).
            setConfirming(true);
            let result = null;
            const deadline = Date.now() + 90000;
            let delay = 1500;
            while (Date.now() < deadline) {
                result = await cbRef.current.verifyPayment(started.order_ref);
                if (result?.outcome && result.outcome !== 'pending') break;
                // Captured per iteration: the timer callback must not close over
                // a `delay` the next pass reassigns (no-loop-func).
                const wait = delay;
                await new Promise((r) => setTimeout(r, wait));
                delay = Math.min(delay * 1.4, 6000);
            }
            setConfirming(false);

            if (result?.outcome === 'paid' || result?.outcome === 'verified') {
                // Same as SavedCardPay: the order ref rides along, so the host
                // can find the receipt on either the deposit or the settlement.
                cbRef.current.onPaid?.({ ...result, order_ref: started.order_ref });
                return;
            }
            if (result?.outcome === 'pending') {
                // NEVER re-enable Pay here: the money may yet land, so a second
                // attempt is the double-charge case. The panel stays locked and
                // the wallet updates itself when the gateway resolves.
                setLocked(true);
                setError('Your bank is still confirming this payment — we’ll update your wallet automatically. Please don’t pay again, and feel free to close this window.');
                cbRef.current.onFailed?.(result);
                return;
            }
            setError(
                sdkStatus === window.NI?.paymentStates?.THREE_DS_FAILURE
                    ? 'Card authentication was not completed — you have not been charged. Enter your card details again to retry.'
                    : 'The payment did not go through — you have not been charged. Check your card details and try again.'
            );
            cbRef.current.onFailed?.(result);
            // A consumed session can never be reused: give them a fresh form.
            resetForRetry();
        } catch (err) {
            const msg = err?.response?.data?.error;
            setError(msg || 'Could not process the card payment. Please try again.');
            // The session (if one was minted) is spent either way.
            resetForRetry();
        } finally {
            // Belt and braces: whatever happened above — thrown, returned or
            // paid — the sheet is not left on the screen.
            showSamsungSheet(false);
            setPaying(false);
        }
    };

    if (phase === 'unavailable') return null;

    const payButton = (
        <button
            type="button"
            className={`hcp__payBtn ${actionSlot ? 'hcp__payBtn--slotted' : ''}`}
            /* fieldsValid implies the SDK is alive and the client has typed a
               valid card — don't also require the onSuccess flag, which the SDK
               sometimes reports late. Card needs valid fields; a wallet (Apple
               Pay) has none of ours to validate — gating on fieldsValid left the
               button dead when Apple Pay was selected (2026-08-13). */
            disabled={disabled || paying || confirming || locked
                || (payMethod === 'CARD' ? !fieldsValid : !walletChosen)}
            onClick={pay}
        >
            {confirming ? 'Confirming your payment…' : paying ? 'Processing…' : payLabel}
        </button>
    );

    return (
        <div className="hcp">
            <div className="hcp__card">
                <div className="hcp__cardHead">
                    <div className="hcp__cardHeadRow">
                        <span className="hcp__cardHeadLabel">🔒 Secure payment</span>
                        <SchemeMarks />
                    </div>

                    {/* INSIDE the head band, not a band of its own. Their SDK
                        already leaves a generous gap above its own tab bar, so a
                        third bordered strip between the marks and that gap was
                        stacking whitespace on whitespace. It belongs with the
                        marks it is explaining. */}
                    {/* NOT gated on phase: the line belongs to the marks
                        above it, not to the SDK's form, and holding it back
                        until the iframes finished left the head band looking
                        half-built for the whole of the load (2026-08-29). */}
                    {walletsEnabled && payMethod === 'CARD' && (
                        <p className="hcp__walletHint">
                            <span className="hcp__walletHintText">
                                {namedWallet === 'apple'
                                    ? <>For Apple&nbsp;Pay select <strong>Digital&nbsp;Payments</strong></>
                                    : namedWallet === 'samsung'
                                        ? <>For Samsung&nbsp;Pay select <strong>Digital&nbsp;Payments</strong></>
                                        : <>For Apple&nbsp;Pay &amp; Samsung&nbsp;Pay select <strong>Digital&nbsp;Payments</strong></>}
                            </span>
                            {/* The SAME tiles as the row above — same size, same
                                frame — so the two lines read as one set. */}
                            <span className="hcp__schemes">
                                {namedWallet !== 'samsung' && (
                                    <span className="hcp__schemeTile">
                                        <img className="hcp__scheme hcp__scheme--glyph hcp__scheme--wide" src={applePayMark} alt="" aria-hidden="true" />
                                    </span>
                                )}
                                {namedWallet !== 'apple' && (
                                    <span className="hcp__schemeTile">
                                        <img className="hcp__scheme hcp__scheme--glyph hcp__scheme--tall" src={samsungPayMark} alt="" aria-hidden="true" />
                                    </span>
                                )}
                            </span>
                        </p>
                    )}
                </div>
                {/* Samsung Pay's own iframe lands here — in a bare,
                    transparent, full-viewport layer with nothing of ours
                    around it. Their frame paints its own backdrop, its own
                    header and its own close; anything we drew was a second
                    modal on top of theirs. Opened and closed by
                    showSamsungSheet, never by the SDK. */}
                <div
                    id={SAMSUNG_MODAL_ID}
                    className="hcp__samsungModal"
                    style={{ display: samsungOpen ? 'block' : 'none' }}
                >
                    <div id={SAMSUNG_MOUNT_ID} className="hcp__samsungMount" />
                    {/* Desktop only — see the stylesheet. Their phone sheet
                        carries its own close; their desktop QR panel does not,
                        and without this the payer sits out a ten-minute timer. */}
                    <button
                        type="button"
                        className="hcp__samsungCancel"
                        onClick={cancelSamsung}
                    >
                        Cancel
                    </button>
                </div>

                <div
                    key={mountKey}
                    id={MOUNT_ID}
                    ref={fieldsRef}
                    className={`hcp__fields ${(paying || confirming || locked) ? 'hcp__fields--busy' : ''}`}
                    style={{
                        height: fieldsHeight,
                        minHeight: fieldsHeight,
                        // The box changes shape when the tab does; easing it
                        // stops the page jumping under the payer's thumb.
                        transition: 'height .18s ease',
                    }}
                >
                    {phase === 'loading' && (
                        <div className="hcp__loadingScrim">
                            <div className="hcp__spinner" aria-label="Loading secure card form" />
                            <div className="hcp__loadingText">Loading secure card form…</div>
                        </div>
                    )}
                </div>
            </div>
            {payMethod === 'CARD' && children}

            {error && <div className="hcp__error">{error}</div>}
            <div className="hcp__secure">🔒 Card details are encrypted and handled by N-Genius (Network International).</div>
            {actionSlot ? createPortal(payButton, actionSlot) : payButton}

            {/* 3DS challenge overlay — the bank's page runs HERE, on our page. */}
            <div className={`hcp__threeDsOverlay ${threeDsActive ? 'is-active' : ''}`}>
                <div className="hcp__threeDsCard">
                    <div className="hcp__threeDsTitle">Confirm with your bank</div>
                    <div id={THREE_DS_MOUNT_ID} className="hcp__threeDsMount" />
                </div>
            </div>
        </div>
    );
}
